import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Sparkles, ChefHat, RefreshCw, Search, CheckCircle2,
  AlertTriangle, AlertCircle, Lightbulb, ChevronDown, ChevronUp,
  Check, X, ExternalLink, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const AdminAIReviewPage = () => {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [reviewing, setReviewing] = useState({});
  const [reviews, setReviews] = useState({});
  const navigate = useNavigate();

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return {}; }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  const fetchRecipes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/recipes`, {
        headers: getAuthHeaders()
      });
      setRecipes(res.data.recipes || []);
    } catch (err) {
      if (err.response?.status === 401) navigate('/admin/login');
      else toast.error('Failed to load recipes');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, navigate]);

  useEffect(() => { fetchRecipes(); }, [fetchRecipes]);

  const runReview = async (slug) => {
    setReviewing(prev => ({ ...prev, [slug]: true }));
    try {
      const res = await axios.post(`${API_URL}/api/admin/recipes/${slug}/ai-review`, {}, {
        headers: getAuthHeaders()
      });
      setReviews(prev => ({ ...prev, [slug]: res.data.review }));
      toast.success(`Review complete for ${slug}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Review failed');
    } finally {
      setReviewing(prev => ({ ...prev, [slug]: false }));
    }
  };

  const applyFixes = async (slug, fields) => {
    try {
      await axios.post(`${API_URL}/api/admin/recipes/${slug}/apply-ai-fixes`,
        { fields },
        { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } }
      );
      toast.success('Fixes applied successfully');
      setReviews(prev => {
        const updated = { ...prev };
        delete updated[slug];
        return updated;
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply fixes');
    }
  };

  const filtered = recipes.filter(r =>
    !search ||
    r.recipe_name?.toLowerCase().includes(search.toLowerCase()) ||
    r.slug?.toLowerCase().includes(search.toLowerCase()) ||
    r.origin_country?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background p-4 md:p-8" data-testid="ai-review-page">
      <Helmet><title>AI Recipe Review - Admin</title></Helmet>

      <div className="max-w-6xl mx-auto mb-8">
        <Link to="/admin" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-4 text-sm">
          <ArrowLeft className="h-4 w-4" /> Back to Admin
        </Link>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-3" data-testid="ai-review-title">
              <Sparkles className="h-6 w-6 text-amber-400" />
              AI Recipe Review
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Use Gemini AI to review recipes for quality, completeness, and cultural accuracy
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchRecipes} data-testid="refresh-recipes">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search recipes by name, slug, or country..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10 bg-card border-border"
            data-testid="ai-review-search"
          />
        </div>
      </div>

      <div className="max-w-6xl mx-auto space-y-3">
        {loading ? (
          <div className="text-center py-16 text-muted-foreground">Loading recipes...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">No recipes found</div>
        ) : (
          filtered.map(recipe => (
            <RecipeReviewCard
              key={recipe.slug}
              recipe={recipe}
              review={reviews[recipe.slug]}
              isReviewing={reviewing[recipe.slug]}
              onReview={() => runReview(recipe.slug)}
              onApplyFixes={(fields) => applyFixes(recipe.slug, fields)}
              navigate={navigate}
            />
          ))
        )}
      </div>
    </div>
  );
};

function RecipeReviewCard({ recipe, review, isReviewing, onReview, onApplyFixes, navigate }) {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (review) setExpanded(true);
  }, [review]);

  return (
    <Card className="bg-card border-border" data-testid={`review-card-${recipe.slug}`}>
      <CardContent className="py-4 px-5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <ChefHat className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="font-medium text-foreground truncate text-sm">{recipe.recipe_name}</span>
            <Badge variant="outline" className="text-xs shrink-0">{recipe.status}</Badge>
            {recipe.origin_country && (
              <span className="text-xs text-muted-foreground shrink-0">{recipe.origin_country}</span>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {review && <ScoreBadge score={review.overall_score} />}
            <Button
              size="sm"
              variant={review ? "outline" : "default"}
              className="h-8 text-xs"
              disabled={isReviewing}
              onClick={onReview}
              data-testid={`review-btn-${recipe.slug}`}
            >
              {isReviewing ? (
                <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Reviewing...</>
              ) : review ? (
                <><RefreshCw className="h-3 w-3 mr-1" /> Re-review</>
              ) : (
                <><Sparkles className="h-3 w-3 mr-1" /> Review</>
              )}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0"
              onClick={() => navigate(`/admin/recipes/${recipe.slug}/edit`)}
              data-testid={`edit-btn-${recipe.slug}`}
            >
              <ExternalLink className="h-3 w-3" />
            </Button>
            {review && (
              <Button
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0"
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            )}
          </div>
        </div>

        {review && expanded && (
          <ReviewDetails review={review} slug={recipe.slug} onApplyFixes={onApplyFixes} />
        )}
      </CardContent>
    </Card>
  );
}

function ScoreBadge({ score }) {
  const color = score >= 8 ? 'text-emerald-400 border-emerald-500/40'
    : score >= 5 ? 'text-amber-400 border-amber-500/40'
    : 'text-red-400 border-red-500/40';
  return (
    <Badge variant="outline" className={`text-xs ${color}`} data-testid="review-score">
      {score}/10
    </Badge>
  );
}

function ReviewDetails({ review, slug, onApplyFixes }) {
  const [applying, setApplying] = useState(false);
  const [selectedFields, setSelectedFields] = useState({});

  const improved = review.improved_fields || {};
  const hasImprovements = Object.keys(improved).length > 0;

  useEffect(() => {
    const initial = {};
    Object.keys(improved).forEach(k => { initial[k] = true; });
    setSelectedFields(initial);
  }, [improved]);

  const handleApply = async () => {
    const fields = {};
    Object.entries(selectedFields).forEach(([k, v]) => {
      if (v && improved[k] !== undefined) fields[k] = improved[k];
    });
    if (Object.keys(fields).length === 0) {
      toast.error('No fields selected');
      return;
    }
    setApplying(true);
    await onApplyFixes(fields);
    setApplying(false);
  };

  const severityIcon = (s) => {
    if (s === 'critical') return <AlertCircle className="h-3.5 w-3.5 text-red-400 shrink-0" />;
    if (s === 'warning') return <AlertTriangle className="h-3.5 w-3.5 text-amber-400 shrink-0" />;
    return <Lightbulb className="h-3.5 w-3.5 text-blue-400 shrink-0" />;
  };

  const severityColor = (s) => {
    if (s === 'critical') return 'border-red-500/30 bg-red-500/5';
    if (s === 'warning') return 'border-amber-500/30 bg-amber-500/5';
    return 'border-blue-500/30 bg-blue-500/5';
  };

  return (
    <div className="mt-4 space-y-4 border-t border-border pt-4" data-testid={`review-details-${slug}`}>
      <p className="text-sm text-muted-foreground italic">{review.summary}</p>

      {review.issues?.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Issues Found</h4>
          {review.issues.map((issue, i) => (
            <div key={i} className={`rounded-md border p-3 ${severityColor(issue.severity)}`}
              data-testid={`issue-${slug}-${i}`}>
              <div className="flex items-start gap-2">
                {severityIcon(issue.severity)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-[10px] h-5">{issue.category}</Badge>
                    <Badge variant="outline" className="text-[10px] h-5">{issue.field}</Badge>
                  </div>
                  <p className="text-sm text-foreground">{issue.description}</p>
                  {issue.suggestion && (
                    <p className="text-xs text-muted-foreground mt-1">
                      <span className="font-medium">Suggestion:</span> {issue.suggestion}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {hasImprovements && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Suggested Improvements
            </h4>
            <Button
              size="sm"
              className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={applying || !Object.values(selectedFields).some(Boolean)}
              onClick={handleApply}
              data-testid={`apply-fixes-${slug}`}
            >
              {applying ? (
                <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Applying...</>
              ) : (
                <><Check className="h-3 w-3 mr-1" /> Apply Selected</>
              )}
            </Button>
          </div>
          {Object.entries(improved).map(([field, value]) => (
            <FieldPreview
              key={field}
              field={field}
              value={value}
              selected={selectedFields[field]}
              onToggle={() => setSelectedFields(prev => ({ ...prev, [field]: !prev[field] }))}
              slug={slug}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FieldPreview({ field, value, selected, onToggle, slug }) {
  const [showFull, setShowFull] = useState(false);
  const preview = typeof value === 'string'
    ? value.slice(0, 150) + (value.length > 150 ? '...' : '')
    : JSON.stringify(value, null, 2).slice(0, 150) + '...';

  return (
    <div className={`rounded-md border p-3 transition-colors ${selected ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-border bg-muted/30'}`}
      data-testid={`improvement-${slug}-${field}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <button onClick={onToggle} className="flex items-center gap-1.5 cursor-pointer"
            data-testid={`toggle-${slug}-${field}`}>
            {selected ? (
              <div className="h-4 w-4 rounded border border-emerald-500 bg-emerald-500 flex items-center justify-center">
                <Check className="h-3 w-3 text-white" />
              </div>
            ) : (
              <div className="h-4 w-4 rounded border border-muted-foreground" />
            )}
          </button>
          <span className="text-sm font-medium text-foreground">{field.replace(/_/g, ' ')}</span>
        </div>
        {(typeof value === 'string' ? value.length > 150 : true) && (
          <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2"
            onClick={() => setShowFull(!showFull)}>
            {showFull ? 'Collapse' : 'Expand'}
          </Button>
        )}
      </div>
      <pre className="text-xs text-muted-foreground whitespace-pre-wrap font-mono bg-background/50 rounded p-2 max-h-60 overflow-y-auto">
        {showFull ? (typeof value === 'string' ? value : JSON.stringify(value, null, 2)) : preview}
      </pre>
    </div>
  );
}

export default AdminAIReviewPage;
